import { useEffect, useRef, useState } from 'react';

const prefersReducedMotion = () =>
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// One shared scroll/resize listener drives every reveal on the page.
const subscribers = new Set();
let listening = false;
let frame = null;

const flush = () => {
    frame = null;
    subscribers.forEach((fn) => fn());
};

const schedule = () => {
    if (frame === null) frame = window.requestAnimationFrame(flush);
};

const subscribe = (fn) => {
    subscribers.add(fn);
    if (!listening) {
        window.addEventListener('scroll', schedule, { passive: true });
        window.addEventListener('resize', schedule);
        listening = true;
    }
    return () => {
        subscribers.delete(fn);
        if (subscribers.size === 0 && listening) {
            window.removeEventListener('scroll', schedule);
            window.removeEventListener('resize', schedule);
            listening = false;
            if (frame !== null) {
                window.cancelAnimationFrame(frame);
                frame = null;
            }
        }
    };
};

/**
 * Reveals an element the first time it scrolls into view.
 * Returns [ref, isRevealed] — spread the ref onto the element and use the
 * flag to toggle an `is-in` class.
 *
 * Uses a direct geometry check rather than IntersectionObserver so it stays
 * correct in environments that throttle observer delivery (background tabs,
 * embedded panes). Failure mode is always "show the content".
 */
export const useReveal = ({ threshold = 0.15 } = {}) => {
    const ref = useRef(null);
    const [revealed, setRevealed] = useState(false);

    useEffect(() => {
        const node = ref.current;
        if (!node) return undefined;

        if (prefersReducedMotion()) {
            setRevealed(true);
            return undefined;
        }

        let done = false;
        const check = () => {
            if (done || !ref.current) return;
            const rect = ref.current.getBoundingClientRect();
            const vh = window.innerHeight || document.documentElement.clientHeight;

            // Visible height of the element, as a fraction of the smaller of
            // (element height, viewport height) so tall sections still qualify.
            const visible = Math.max(0, Math.min(rect.bottom, vh) - Math.max(rect.top, 0));
            const basis = Math.min(rect.height, vh) || 1;

            if (visible / basis >= threshold) {
                done = true;
                setRevealed(true);
                unsubscribe();
            }
        };

        const unsubscribe = subscribe(check);
        check();

        // Backstop: if anything above is wrong, never leave content hidden.
        const safety = window.setTimeout(() => {
            if (!done) {
                done = true;
                setRevealed(true);
                unsubscribe();
            }
        }, 2500);

        return () => {
            unsubscribe();
            window.clearTimeout(safety);
        };
    }, [threshold]);

    return [ref, revealed];
};

/**
 * Tracks how far the viewport has travelled through an element, 0 → 1.
 * Used for the scroll-driven Evolve Loop.
 */
export const useScrollProgress = () => {
    const ref = useRef(null);
    const [progress, setProgress] = useState(0);

    useEffect(() => {
        const node = ref.current;
        if (!node) return undefined;

        if (prefersReducedMotion()) {
            setProgress(1);
            return undefined;
        }

        const measure = () => {
            if (!ref.current) return;
            const rect = ref.current.getBoundingClientRect();
            const total = rect.height + window.innerHeight;
            const seen = window.innerHeight - rect.top;
            setProgress(Math.min(1, Math.max(0, seen / total)));
        };

        const unsubscribe = subscribe(measure);
        measure();
        return unsubscribe;
    }, []);

    return [ref, progress];
};
